import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersKalkBayPage() {
  const [activeTab, setActiveTab] = useState('overview');

  useSEO({
    title: 'Kalk Bay Painters | Professional Painting Services | 19+ Years Experience',
    description: 'Expert painters in Kalk Bay offering premium residential and commercial painting services. Specializing in coastal properties, heritage homes, and modern renovations. Free quotes, quality workmanship, and professional service guaranteed.',
    keywords: 'Kalk Bay painters, painters Kalk Bay, painting services Kalk Bay, house painters Kalk Bay, residential painters, commercial painters, coastal property painters, heritage home painters, professional painters',
    ogImage: 'https://public.readdy.ai/ai/img_res/9b2077abea50127be8c1745daec2e9fd.jpg',
    schema: [
      generateLocalBusinessSchema({
        name: 'Painters Kalk Bay',
        description: 'Professional painting services in Kalk Bay specializing in coastal residential and commercial properties with over 19 years of experience.',
        address: {
          streetAddress: 'Kalk Bay',
          addressLocality: 'Cape Town',
          addressRegion: 'Western Cape',
          postalCode: '7975',
          addressCountry: 'ZA'
        },
        geo: {
          latitude: '-34.1289',
          longitude: '18.4489'
        },
        areaServed: ['Kalk Bay', 'Fish Hoek', 'Muizenberg', 'St James', 'Simons Town', 'Clovelly', 'Glencairn'],
        priceRange: '$$-$$$',
        image: 'https://readdy.ai/api/search-image?query=professional%20painting%20company%20team%20working%20on%20coastal%20residential%20property%20modern%20equipment%20clean%20white%20background%20high%20quality%20professional%20photography&width=800&height=600&seq=kalkbay-business&orientation=landscape'
      }),
      generateServiceSchema({
        name: 'Coastal Property Painting Services',
        description: 'Premium painting services for coastal homes and heritage properties in Kalk Bay',
        provider: 'Painters Kalk Bay',
        areaServed: 'Kalk Bay, Cape Town',
        serviceType: 'Painting Service'
      })
    ]
  });

  const services = [
    {
      title: 'Coastal Property Painting',
      description: 'Specialized painting services for coastal homes using weather-resistant coatings and marine-grade materials.',
      icon: 'ri-ship-line'
    },
    {
      title: 'Heritage Home Restoration',
      description: 'Expert restoration and painting of heritage properties maintaining historical authenticity and character.',
      icon: 'ri-building-4-line'
    },
    {
      title: 'Interior Painting',
      description: 'Professional interior painting with premium finishes, color consultation, and attention to detail.',
      icon: 'ri-paint-brush-line'
    },
    {
      title: 'Exterior Weatherproofing',
      description: 'Durable exterior painting with salt-resistant coatings perfect for seaside properties.',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Commercial Properties',
      description: 'Professional painting services for restaurants, shops, offices, and commercial buildings.',
      icon: 'ri-store-2-line'
    },
    {
      title: 'Deck & Woodwork',
      description: 'Specialized painting and staining services for decks, balconies, and wooden structures.',
      icon: 'ri-hammer-line'
    }
  ];

  const reviews = [
    {
      name: 'Emma Richardson',
      rating: 5,
      text: 'Outstanding work on our coastal cottage! The team understood the challenges of painting near the ocean and used the right materials. Our home looks beautiful and is well-protected from the elements.',
      date: '2 weeks ago'
    },
    {
      name: 'James Peterson',
      rating: 5,
      text: 'We hired them to restore our heritage home in Kalk Bay and they did an incredible job. They respected the historical features while giving it a fresh, modern look. Highly professional team.',
      date: '1 month ago'
    },
    {
      name: 'Sophie van Niekerk',
      rating: 5,
      text: 'Best painters in the False Bay area! They painted our restaurant and the results are stunning. Professional, efficient, and the quality is exceptional. Definitely recommend!',
      date: '1 month ago'
    },
    {
      name: 'Michael O\'Brien',
      rating: 5,
      text: 'Excellent service from start to finish. They painted both interior and exterior of our home and handled everything professionally. Great communication and attention to detail.',
      date: '2 months ago'
    },
    {
      name: 'Linda Jacobs',
      rating: 5,
      text: 'Transformed our beachfront property beautifully! The painters were respectful, clean, and delivered exceptional quality. Their expertise in coastal painting really shows.',
      date: '2 months ago'
    },
    {
      name: 'David Thompson',
      rating: 5,
      text: 'Professional team that takes pride in their work. They completed our project on time and the finish is perfect. Worth every cent for the quality you receive.',
      date: '3 months ago'
    }
  ];

  const galleryImages = [
    {
      url: 'https://readdy.ai/api/search-image?query=coastal%20cottage%20exterior%20freshly%20painted%20white%20and%20blue%20ocean%20views%20clean%20simple%20background%20professional%20photography%20bright%20daylight%20seaside%20property&width=600&height=400&seq=kalkbay-gallery-1&orientation=landscape',
      title: 'Coastal Cottage'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=heritage%20home%20exterior%20freshly%20painted%20traditional%20colors%20victorian%20architecture%20clean%20simple%20background%20professional%20photography%20natural%20lighting&width=600&height=400&seq=kalkbay-gallery-2&orientation=landscape',
      title: 'Heritage Home'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=modern%20interior%20living%20room%20freshly%20painted%20coastal%20colors%20contemporary%20furniture%20clean%20simple%20background%20professional%20photography%20natural%20lighting&width=600&height=400&seq=kalkbay-gallery-3&orientation=landscape',
      title: 'Interior Living Space'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=restaurant%20interior%20freshly%20painted%20modern%20design%20dining%20area%20clean%20simple%20background%20professional%20photography%20bright%20natural%20light&width=600&height=400&seq=kalkbay-gallery-4&orientation=landscape',
      title: 'Commercial Restaurant'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=wooden%20deck%20freshly%20stained%20ocean%20views%20outdoor%20furniture%20clean%20simple%20background%20professional%20photography%20bright%20daylight&width=600&height=400&seq=kalkbay-gallery-5&orientation=landscape',
      title: 'Deck Restoration'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=beachfront%20property%20exterior%20painted%20white%20modern%20coastal%20architecture%20clean%20simple%20background%20professional%20photography%20natural%20lighting&width=600&height=400&seq=kalkbay-gallery-6&orientation=landscape',
      title: 'Beachfront Property'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-bold" style={{ color: '#e9f638' }}>
              Painters Directory
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-gray-300 transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-gray-300 transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-gray-300 transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="px-6 py-2 rounded-lg whitespace-nowrap transition-colors" style={{ backgroundColor: '#e9f638', color: '#000' }}>
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=kalk%20bay%20harbor%20cape%20town%20coastal%20village%20colorful%20houses%20ocean%20views%20clean%20simple%20background%20professional%20photography%20bright%20natural%20lighting%20seaside%20community&width=1920&height=600&seq=kalkbay-hero&orientation=landscape"
            alt="Kalk Bay Painters"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div className="w-full">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Painters Kalk Bay</h1>
            <p className="text-xl md:text-2xl text-white mb-6">Expert Painting Services for Coastal Properties</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/get-a-quote"
                className="px-8 py-3 rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
                style={{ backgroundColor: '#e9f638', color: '#000' }}
              >
                Get Free Quote
              </Link>
              <a
                href="tel:0219876543"
                className="px-8 py-3 bg-white text-black rounded-lg font-semibold transition-all hover:scale-105 whitespace-nowrap"
              >
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 sticky top-16 bg-white z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {['overview', 'services', 'gallery', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? 'border-black text-black'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'overview' && (
          <div className="space-y-12">
            {/* About Section */}
            <section>
              <h2 className="text-3xl font-bold mb-6">About Painters Kalk Bay</h2>
              <div className="prose max-w-none space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Welcome to Painters Kalk Bay, your trusted partner for professional painting services in one of Cape Town's most charming and picturesque coastal villages. With over 19 years of experience in the painting industry, we have established ourselves as the leading painting contractor for coastal properties, heritage homes, and commercial establishments in the Kalk Bay area and surrounding False Bay communities.
                </p>
                <p>
                  Kalk Bay is renowned for its unique character, colorful fishing harbor, heritage architecture, and vibrant community atmosphere. Our team of expert painters understands the special requirements of painting in this coastal environment, from dealing with salt air and moisture to preserving the historical charm of older properties while incorporating modern finishes and techniques. We take pride in delivering exceptional results that enhance the beauty of your property while providing long-lasting protection against the harsh coastal elements.
                </p>
                <p>
                  Our company was founded on the principles of quality craftsmanship, professional service, and deep respect for the unique character of Kalk Bay and its surrounding areas. Over the years, we have built a reputation for excellence by consistently delivering outstanding results on every project, whether it's a heritage cottage restoration, modern home renovation, beachfront property maintenance, or commercial establishment upgrade. We understand that your property is not just a building but a part of this special community, and we treat every project with the care and attention it deserves.
                </p>
                <p>
                  What sets Painters Kalk Bay apart from other painting contractors is our specialized expertise in coastal property painting, our commitment to using marine-grade and weather-resistant materials, and our team of highly skilled painters who are experienced in working with both heritage and modern properties. We work closely with homeowners, property managers, heritage consultants, and business owners to ensure that every aspect of the painting project meets or exceeds expectations while respecting the unique character of the area.
                </p>
                <p>
                  Our expertise extends across all types of painting projects, including interior painting, exterior painting, heritage restoration, weatherproofing, decorative finishes, and commercial painting. We are particularly experienced in working with coastal properties that require specialized coatings and preparation techniques to withstand salt air, wind, and moisture. Whether you're looking to restore a Victorian cottage, refresh a modern beachfront home, or repaint your restaurant or shop, we have the skills, experience, and resources to deliver exceptional results.
                </p>
                <p>
                  We understand that painting projects can be disruptive, especially in a close-knit community like Kalk Bay. That's why we work efficiently to minimize inconvenience while maintaining our high standards of quality. Our team is fully insured, professionally trained, and committed to maintaining a clean and safe work environment. We use environmentally friendly materials where possible, dust-free sanding equipment, protective coverings, and proper ventilation to ensure that your property and the surrounding area remain clean and comfortable throughout the painting process.
                </p>
                <p>
                  At Painters Kalk Bay, we believe in building long-term relationships with our clients and being an active part of the community. Many of our clients return to us for all their painting needs and recommend us to their neighbors, friends, and fellow business owners. This word-of-mouth reputation is something we value highly and work hard to maintain with every project we undertake. We're proud to contribute to maintaining and enhancing the beauty of Kalk Bay, one property at a time.
                </p>
                <p>
                  Our service area includes Kalk Bay, Fish Hoek, Muizenberg, St James, Simons Town, Clovelly, Glencairn, and surrounding False Bay communities. We are intimately familiar with the local architecture, coastal climate conditions, and community standards, which allows us to provide expert advice and recommendations tailored to your specific needs. Whether you own a heritage cottage, modern home, beachfront property, or commercial establishment, we have the expertise to deliver results that exceed your expectations while respecting the unique character of this special coastal area.
                </p>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-3xl font-bold mb-6">Why Choose Painters Kalk Bay?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-medal-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">19+ Years Experience</h3>
                    <p className="text-gray-600">Extensive experience in coastal property and heritage home painting throughout Kalk Bay and False Bay.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-ship-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Coastal Specialists</h3>
                    <p className="text-gray-600">Specialized expertise in coastal property painting using marine-grade and weather-resistant materials.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-building-4-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Heritage Expertise</h3>
                    <p className="text-gray-600">Expert restoration and painting of heritage properties maintaining historical authenticity and character.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Fully Insured</h3>
                    <p className="text-gray-600">Comprehensive insurance coverage for your peace of mind and property protection.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-leaf-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Eco-Friendly Options</h3>
                    <p className="text-gray-600">Environmentally friendly paint options and sustainable practices for conscious property owners.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#e9f638' }}>
                    <i className="ri-community-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Local Community</h3>
                    <p className="text-gray-600">Proud members of the Kalk Bay community committed to maintaining its unique character and beauty.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Service Areas */}
            <section>
              <h2 className="text-3xl font-bold mb-6">Service Areas</h2>
              <p className="text-gray-700 mb-4">
                We proudly serve Kalk Bay and surrounding False Bay areas including:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Kalk Bay', 'Fish Hoek', 'Muizenberg', 'St James', 'Simons Town', 'Clovelly', 'Glencairn', 'Noordhoek'].map((area) => (
                  <div key={area} className="flex items-center space-x-2">
                    <i className="ri-map-pin-line" style={{ color: '#e9f638' }}></i>
                    <span className="text-gray-700">{area}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6">Our Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg mb-4" style={{ backgroundColor: '#e9f638' }}>
                      <i className={`${service.icon} text-2xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Detailed Services */}
            <section className="prose max-w-none">
              <h2 className="text-3xl font-bold mb-6">Comprehensive Painting Solutions</h2>
              <div className="space-y-6 text-gray-700">
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Coastal Property Painting</h3>
                  <p>
                    Our coastal property painting services are specifically designed for homes and buildings in Kalk Bay's unique seaside environment. We understand the challenges posed by salt air, wind, and moisture, and use specialized marine-grade coatings and weather-resistant materials to ensure your property is beautifully protected. Our process includes thorough surface preparation, salt removal, crack repair, waterproofing, and application of premium exterior paints designed specifically for coastal conditions. We also provide ongoing maintenance programs to keep your coastal property looking pristine year-round.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Heritage Home Restoration</h3>
                  <p>
                    Kalk Bay is home to many beautiful heritage properties that require specialized care and expertise. Our heritage home restoration services combine traditional painting techniques with modern materials to preserve the historical character of your property while ensuring long-lasting protection. We work with heritage consultants and follow conservation guidelines to maintain authenticity while improving durability. From Victorian cottages to Edwardian homes, we have the expertise to restore and enhance your heritage property with respect for its historical significance.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Interior Painting Excellence</h3>
                  <p>
                    Transform your interior spaces with our professional interior painting services. We offer expert color consultation, premium finishes, feature walls, and decorative painting techniques. Whether you're looking for a fresh coastal palette, bold contemporary colors, or classic neutral tones, our team has the skills to bring your vision to life. We use low-VOC and eco-friendly paints where possible, ensuring a healthy indoor environment for you and your family. Our meticulous attention to detail ensures perfect lines, smooth finishes, and beautiful results that last.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-3">Commercial Painting Services</h3>
                  <p>
                    Kalk Bay's vibrant commercial district includes restaurants, shops, galleries, and offices that require professional painting services. We specialize in commercial painting projects that minimize disruption to your business while delivering exceptional results. From restaurant interiors to shop facades, we understand the importance of maintaining your business's appearance and brand identity. We work flexible hours to accommodate your business schedule and complete projects efficiently without compromising quality.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'gallery' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Work</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((image, index) => (
                <div key={index} className="group relative overflow-hidden rounded-lg aspect-video">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover object-top transition-transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <p className="text-white font-semibold text-lg">{image.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Customer Reviews</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((review, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{review.name}</h3>
                      <p className="text-sm text-gray-500">{review.date}</p>
                    </div>
                    <div className="flex space-x-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill" style={{ color: '#e9f638' }}></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="py-16" style={{ backgroundColor: '#e9f638' }}>
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Property?</h2>
          <p className="text-xl mb-8">Get a free, no-obligation quote for your painting project today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/get-a-quote"
              className="px-8 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              Request Free Quote
            </Link>
            <a
              href="tel:0219876543"
              className="px-8 py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#e9f638' }}>Painters Kalk Bay</h3>
              <p className="text-gray-400">Professional painting services for coastal properties in Kalk Bay and surrounding areas.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/get-a-quote" className="text-gray-400 hover:text-white transition-colors">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Coastal Property Painting</li>
                <li>Heritage Home Restoration</li>
                <li>Interior Painting</li>
                <li>Commercial Properties</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line"></i>
                  <span>021 987 6543</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Kalk Bay, Cape Town</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Painters Kalk Bay. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="https://leaderr.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Powered by leaderr
              </a>
              <a href="https://seostudio.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}